'use client';

import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import type { AdpopcornPlacementKey } from '@/constants/offerwall';
import { ADPOPCORN_PLACEMENT_IDS } from '@/constants/offerwall';
import { useAdUserId } from '@/hooks/useAdUserId';
import { useVerificationCheck } from '@/hooks/useVerificationCheck';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useGlobalStore } from '@/stores/GlobalStore';
import { useAdGuards } from './useAdGuards';

//* 혜택 오퍼월 임시 재정비 — 모든 진입점(퀵메뉴·하단탭·웰마켙 바닥내비·마이페이지) 클릭 시
//* 재정비 안내 모달만 띄운다. 재개 시 이 플래그와 BenefitMaintenanceModal을 제거한다.
const IS_BENEFIT_MAINTENANCE = true;
export type AdpopcornStatus = 'unsupported' | 'app-install' | 'login' | 'ready';
interface UseAdpopcornOptions {
  placement: AdpopcornPlacementKey;
}
interface UseAdpopcornPlacementReturn {
  status: AdpopcornStatus;
  placementId: string | null;
  isExternalWebview: boolean;
}
interface UseAdpopcornReturn {
  status: AdpopcornStatus;
  placementId: string | null;
  isExternalWebview: boolean;
  onClick: () => void;
  isOpening: boolean;
}
export const useAdpopcornPlacement = t0 => {
  const $ = _c(4);
  const {
    placement
  } = t0;
  const {
    isMounted,
    isWelloApp,
    isExternalWebview,
    osType
  } = useAdGuards();
  const memberOAuth = useAuthStore(_temp);
  const placementConfig = ADPOPCORN_PLACEMENT_IDS[placement];
  const placementId = osType === "ios" || osType === "android" ? placementConfig[osType] : null;
  let t1;
  bb0: {
    if (!isMounted || isExternalWebview) {
      t1 = "unsupported";
      break bb0;
    }
    if (!isWelloApp) {
      t1 = "app-install";
      break bb0;
    }
    if (!memberOAuth) {
      t1 = "login";
      break bb0;
    }
    if (!placementId) {
      t1 = "unsupported";
      break bb0;
    }
    t1 = "ready";
  }
  const status = t1;
  let t2;
  if ($[0] !== isExternalWebview || $[1] !== placementId || $[2] !== status) {
    t2 = {
      status,
      placementId,
      isExternalWebview
    };
    $[0] = isExternalWebview;
    $[1] = placementId;
    $[2] = status;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
export const useAdpopcorn = t0 => {
  const $ = _c(22);
  const {
    placement
  } = t0;
  let t1;
  if ($[0] !== placement) {
    t1 = {
      placement
    };
    $[0] = placement;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    status,
    placementId,
    isExternalWebview
  } = useAdpopcornPlacement(t1);
  const adUserId = useAdUserId();
  const {
    toast
  } = useToast();
  const t2 = status === "ready";
  let t3;
  if ($[2] !== t2) {
    t3 = {
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    checkVerification,
    isLoading: isVerificationLoading
  } = useVerificationCheck(t3);
  const setIsShowAppInstallConfirmModal = useGlobalStore(_temp2);
  const setIsShowLoginRedirectionConfirmModal = useGlobalStore(_temp3);
  const setIsShowBenefitMaintenanceModal = useGlobalStore(_temp4);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      type: MESSAGE_KEYWORD.OPEN_ADPOPCORN_BRIDGE
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    request,
    isPending
  } = useHandleApp(t4);
  let t5;
  if ($[5] !== adUserId || $[6] !== checkVerification || $[7] !== isVerificationLoading || $[8] !== placementId || $[9] !== request || $[10] !== setIsShowAppInstallConfirmModal || $[11] !== setIsShowBenefitMaintenanceModal || $[12] !== setIsShowLoginRedirectionConfirmModal || $[13] !== status || $[14] !== toast) {
    t5 = () => {
      if (IS_BENEFIT_MAINTENANCE) {
        setIsShowBenefitMaintenanceModal(true);
        return;
      }
      if (status === "app-install") {
        setIsShowAppInstallConfirmModal(true);
        return;
      }
      if (status === "login") {
        setIsShowLoginRedirectionConfirmModal(true);
        return;
      }
      if (status === "ready") {
        if (isVerificationLoading) {
          return;
        }
        if (checkVerification()) {
          return;
        }
        if (!adUserId) {
          toast({
            message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694",
            type: "fail"
          });
          return;
        }
        request({
          placementId,
          userId: adUserId
        });
      }
    };
    $[5] = adUserId;
    $[6] = checkVerification;
    $[7] = isVerificationLoading;
    $[8] = placementId;
    $[9] = request;
    $[10] = setIsShowAppInstallConfirmModal;
    $[11] = setIsShowBenefitMaintenanceModal;
    $[12] = setIsShowLoginRedirectionConfirmModal;
    $[13] = status;
    $[14] = toast;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const onClick = t5;
  let t6;
  if ($[16] !== isExternalWebview || $[17] !== isPending || $[18] !== onClick || $[19] !== placementId || $[20] !== status) {
    t6 = {
      status,
      placementId,
      isExternalWebview,
      onClick,
      isOpening: isPending
    };
    $[16] = isExternalWebview;
    $[17] = isPending;
    $[18] = onClick;
    $[19] = placementId;
    $[20] = status;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
};
function _temp(state) {
  return state.myInfo?.memberOAuth;
}
function _temp2(state) {
  return state.setIsShowAppInstallConfirmModal;
}
function _temp3(state_0) {
  return state_0.setIsShowLoginRedirectionConfirmModal;
}
function _temp4(state_1) {
  return state_1.setIsShowBenefitMaintenanceModal;
}